/**
 * Weg-Ribbon: Fabric-Factory für das Mittellinien-Weg-Objekt (#720)
 *
 * Der Weg ist ein offener Fabric-`path` entlang der Mittellinie, dargestellt als
 * Band über die **Stroke-Breite** (`strokeWidth = Wegbreite`, `fill: null`). Die
 * Endkappe ist gerade (`strokeLineCap: 'butt'`), der Verlauf bestimmt den
 * Linienübergang: `eckig` = `miter`, `kurvig` = `round`.
 *
 * Die reine Pfad-/Maß-Logik liegt in wegCenterline.js; hier kommt nur das
 * Fabric-abhängige Zusammenbauen dazu, damit Zeichner (Chunk B), Panel (C) und
 * Vertex-Bearbeitung (E) denselben Weg regenerieren.
 */

import { Path } from 'fabric';
import { getObjectTypeById } from '../../../config';
import { centerlineToPathData, WEG_VERLAUF, isValidVerlauf } from './wegCenterline';

/** Fabric-`strokeLineJoin` passend zum Verlauf. */
export const wegLineJoin = (verlauf) =>
  verlauf === WEG_VERLAUF.ECKIG ? 'miter' : 'round';

/**
 * Löst die Weg-Defaults (Breite, Verlauf, Farbe) aus der Config auf.
 * Übergebene Werte haben Vorrang; fehlt etwas, greift der Config-Default.
 *
 * @param {string} objectTypeId - i. d. R. 'weg'
 * @param {{ width?: number, verlauf?: string, color?: string }} overrides
 */
export const resolveWegStyle = (objectTypeId = 'weg', overrides = {}) => {
  const config = getObjectTypeById(objectTypeId);
  const mvp = config?.properties?.mvp || {};
  const width = overrides.width ?? mvp.width?.defaultValue ?? 100;
  const verlaufRaw = overrides.verlauf ?? mvp.verlauf?.defaultValue ?? WEG_VERLAUF.KURVIG;
  const verlauf = isValidVerlauf(verlaufRaw) ? verlaufRaw : WEG_VERLAUF.KURVIG;
  const color = overrides.color ?? mvp.color?.defaultValue ?? config?.defaultStyle?.stroke ?? '#A08060';
  return { width, verlauf, color };
};

/**
 * Erzeugt das Fabric-Weg-Objekt aus einer Mittellinie.
 *
 * @param {Array<{x: number, y: number}>} centerline - Mittellinien-Punkte (Canvas-px)
 * @param {{ width?: number, verlauf?: string, color?: string }} [style]
 * @param {string} [objectTypeId='weg']
 * @returns {Path|null} Fabric-Path oder null bei < 2 Punkten
 */
export const createWegPath = (centerline, style = {}, objectTypeId = 'weg') => {
  const { width, verlauf, color } = resolveWegStyle(objectTypeId, style);
  const pathData = centerlineToPathData(centerline, verlauf);
  if (!pathData) return null;

  const path = new Path(pathData, {
    originX: 'left',
    originY: 'top',
    selectable: true,
    evented: true,
    objectCaching: true,
    strokeUniform: true, // Breite konstant beim Skalieren
    perPixelTargetFind: true, // pixelgenaue Trefferprüfung inkl. Stroke-Ribbon
    fill: null, // das Band IST der Stroke; ein Fill würde den offenen Pfad falsch füllen
    stroke: color,
    strokeWidth: width, // Editor: 1 px = 1 cm ⇒ Breite in cm = Stroke-Breite in px
    strokeLineCap: 'butt', // gerade Wegenden
    strokeLineJoin: wegLineJoin(verlauf),
    opacity: 1,
  });

  // Fabric verschachtelt `data` über den Konstruktor nicht zuverlässig — direkt setzen.
  path.objectType = 'weg';
  path.data = {
    shapeType: 'centerline', // unterscheidet neuen Weg von Legacy-Bezier/-Polygon
    objectTypeId,
    gardenObjectType: objectTypeId,
    isActive: true,
    createdAt: new Date().toISOString(),
    centerline,
    width,
    verlauf,
    // Für Selektions-Restore
    originalStroke: color,
    originalStrokeWidth: width,
  };

  return path;
};

/**
 * Regeneriert Pfad + Stroke eines bestehenden Weg-Objekts aus seinen `data`-Werten.
 * Genutzt von Panel (Breite/Verlauf ändern) und Vertex-Bearbeitung.
 *
 * @param {Path} path - Fabric-Weg-Objekt (data.centerline/width/verlauf)
 * @returns {boolean} true, wenn neu gesetzt wurde
 */
export const applyWegGeometry = (path) => {
  if (!path || path.data?.shapeType !== 'centerline') return false;
  const { centerline, width, verlauf } = path.data;
  const pathData = centerlineToPathData(centerline, verlauf);
  if (!pathData) return false;

  path.set({
    path: new Path(pathData).path,
    strokeWidth: width,
    strokeLineJoin: wegLineJoin(verlauf),
  });
  path.setCoords();
  path.canvas?.requestRenderAll();
  return true;
};
